// An offer's terms as the send card shows them: the main contract's facts (read-only but for
// the origination fee) and the refinance offer's terms (editable). Shared with the assistant's
// cards, which opt into a layout by the component's own width (layout="auto").
import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { OFFER_FACTOR_STEP } from "@/utils/offerCalculations";
import {
  formatRefinanceMoneyInput,
  parseMoneyInput,
  type RefinanceCalcValues,
} from "@/utils/refinanceOfferCalc";
import { formatCurrency, formatPercentPoints } from "../originationFee";

/** Muted labels, darker values: keeps Offer / Business / Owner in a grey / black hierarchy. */
export const TERMS_LABEL = "font-medium text-muted-foreground";
const VALUE_CLASS = "font-semibold tabular-nums text-foreground";
/** Each row is a fixed label lane and its value. */
const ROW = "grid min-h-8 grid-cols-[8.75rem_minmax(0,1fr)] items-center gap-x-3 text-sm";
const COLUMN = "space-y-2";
const VALUE_CONTROL =
  "h-8 w-full max-w-[9.5rem] rounded-md border border-input bg-background px-2 text-right text-sm font-semibold tabular-nums text-foreground outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/20";
/** Fixed outer width; the input flexes so values like 10.75 fit and the stepper stays flush right. */
const ORIGINATION_CONTROL =
  "flex h-8 w-[5.75rem] items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20";

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/** Contract prefills sometimes use Day / Week / Month; show the full cadence labels. */
const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  day: "Daily",
  weekly: "Weekly",
  week: "Weekly",
  monthly: "Monthly",
  month: "Monthly",
};

export function displayFrequency(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return FREQUENCY_LABELS[trimmed.toLowerCase()] ?? trimmed;
}

/** A value whose tooltip says where it is edited. Use inside a TooltipProvider. */
export const HintValue: React.FC<{ hint: string; children: React.ReactNode }> = ({
  hint,
  children,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="block max-w-full truncate text-left text-sm font-semibold text-foreground hover:underline"
      >
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent>{hint}</TooltipContent>
  </Tooltip>
);

export const FactRow: React.FC<{
  label: string;
  children: React.ReactNode;
  error?: string | null;
  highlight?: boolean;
}> = ({ label, children, error, highlight }) => (
  <div className={highlight ? `${ROW} rounded-md bg-muted/60 px-2` : ROW}>
    <dt className={TERMS_LABEL}>{label}</dt>
    <dd className="min-w-0">
      {children}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </dd>
  </div>
);

/** A fact that is always listed: "—" while it has no value. */
export const TextFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <FactRow label={label}>
    <span className={VALUE_CLASS}>{value || "—"}</span>
  </FactRow>
);

/** A fact listed only once it has a value; with a hint, the value says where it is edited. */
const OptionalFact: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => {
  if (!value) return null;
  if (!hint) return <TextFact label={label} value={value} />;
  return (
    <FactRow label={label}>
      <HintValue hint={hint}>{value}</HintValue>
    </FactRow>
  );
};

const MoneyEdit: React.FC<{
  label: string;
  value: number;
  onChange: (n: number) => void;
  error?: string | null;
  disabled?: boolean;
}> = ({ label, value, onChange, error, disabled }) => (
  <FactRow label={label} error={error}>
    <div
      className={`flex h-8 w-full max-w-[9.5rem] items-stretch overflow-hidden rounded-md border bg-background focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20 ${
        error ? "border-destructive" : "border-input"
      }`}
    >
      <span className="flex items-center pl-2 text-sm font-semibold text-muted-foreground">$</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        disabled={disabled}
        className="min-w-0 flex-1 bg-transparent px-1.5 text-right text-sm font-semibold tabular-nums text-foreground outline-none disabled:cursor-not-allowed disabled:opacity-50"
        value={formatRefinanceMoneyInput(value)}
        onChange={(event) => onChange(parseMoneyInput(event.target.value))}
      />
    </div>
  </FactRow>
);

const NumberEdit: React.FC<{
  label: string;
  value: number;
  step?: string;
  onChange: (n: number) => void;
  disabled?: boolean;
}> = ({ label, value, step, onChange, disabled }) => (
  <FactRow label={label}>
    <input
      type="number"
      step={step}
      inputMode="decimal"
      aria-label={label}
      disabled={disabled}
      className={`${VALUE_CONTROL} disabled:cursor-not-allowed disabled:opacity-50`}
      value={value || ""}
      onChange={(event) => onChange(Number(event.target.value) || 0)}
    />
  </FactRow>
);

/** Section chrome shared by Offer / Business / Owner. */
export const TermsSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="space-y-2 border-t pt-3">
    <h4 className="text-sm font-semibold text-foreground">{title}</h4>
    {children}
  </section>
);

/** The origination fee % field: typed, or stepped by 0.25. */
export interface OriginationInput {
  pct: number;
  /** What is typed, which may not parse yet. */
  input: string;
  onInput: (raw: string) => void;
  onStep: (delta: number) => void;
  /** Settles what was typed (`useOriginationInput`). */
  onBlur?: () => void;
}

const ORIGINATION_LABEL = "Origination Fee %";

/** Editable with an OriginationInput; a plain percentage reads as a fact. */
export const OriginationPct: React.FC<{ origination: OriginationInput | number }> = ({
  origination,
}) => {
  if (typeof origination === "number") {
    return <TextFact label={ORIGINATION_LABEL} value={`${formatPercentPoints(origination)}%`} />;
  }
  const { pct, input, onInput, onStep, onBlur } = origination;
  return (
    <FactRow label={ORIGINATION_LABEL}>
      <div className={ORIGINATION_CONTROL}>
        <input
          type="text"
          inputMode="decimal"
          aria-label="Origination Fee percent"
          className="min-w-0 flex-1 bg-transparent px-1.5 text-right text-sm font-semibold tabular-nums text-foreground outline-none"
          value={input}
          onChange={(event) => onInput(event.target.value)}
          onBlur={onBlur}
        />
        <span className="flex shrink-0 items-center pr-1 text-sm font-semibold text-muted-foreground">
          %
        </span>
        <div className="flex w-5 shrink-0 flex-col border-l border-input">
          <button
            type="button"
            aria-label="Increase origination fee by 0.25%"
            onClick={() => onStep(0.25)}
            className="flex flex-1 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            aria-label="Decrease origination fee by 0.25%"
            onClick={() => onStep(-0.25)}
            className="flex flex-1 items-center justify-center border-t border-input text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
      <span className="sr-only">{pct}</span>
    </FactRow>
  );
};

/**
 * "page" (default): two columns from the viewport's sm breakpoint, as on the send card. "auto":
 * two columns from 36rem of the component's own width, for cards narrower than the viewport.
 */
export type OfferTermsLayout = "page" | "auto";

const SplitColumns: React.FC<{
  left: React.ReactNode;
  right: React.ReactNode;
  layout: OfferTermsLayout;
}> = ({ left, right, layout }) => {
  const columns = (
    <>
      <div className={COLUMN}>{left}</div>
      <div className={COLUMN}>{right}</div>
    </>
  );
  return layout === "auto" ? (
    <div className="[container:offer-terms/inline-size]">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-2 [@container_offer-terms_(min-width:36rem)]:grid-cols-2">
        {columns}
      </dl>
    </div>
  ) : (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">{columns}</dl>
  );
};

/** A main contract offer's facts, as display strings ("" when unknown). */
export interface OfferFactValues {
  fundingAmount: string;
  payback: string;
  disbursed: string;
  specifiedPercentage: string;
  payment: string;
  frequency: string;
  originationFee: string;
}

export interface OfferFactsProps {
  facts: OfferFactValues;
  origination: OriginationInput | number;
  /** Where the facts are edited, shown on each one's tooltip. */
  hint?: string;
  layout?: OfferTermsLayout;
}

/**
 * The main contract's Offer section: funding, payback, disbursed, specified percentage, payment
 * and frequency (each listed once known), the origination fee % and the fee. Use inside a
 * TooltipProvider when a hint is given.
 */
export function OfferFacts({ facts, origination, hint, layout = "page" }: OfferFactsProps) {
  return (
    <TermsSection title="Offer">
      <SplitColumns
        layout={layout}
        left={
          <>
            <OptionalFact label="Funding amount" value={facts.fundingAmount} hint={hint} />
            <OptionalFact label="Payback" value={facts.payback} hint={hint} />
            <OptionalFact label="Disbursed" value={facts.disbursed} hint={hint} />
            <OptionalFact
              label="Specified percentage"
              value={facts.specifiedPercentage}
              hint={hint}
            />
          </>
        }
        right={
          <>
            <OptionalFact label="Payment" value={facts.payment} hint={hint} />
            <OptionalFact label="Frequency" value={facts.frequency} hint={hint} />
            <OriginationPct origination={origination} />
            <TextFact label="Origination Fee" value={facts.originationFee} />
          </>
        }
      />
    </TermsSection>
  );
}

/** What editing the refinance terms does: each field's new value, as typed. */
export interface RefinanceTermsEdit {
  onFunding: (amount: number) => void;
  onFactor: (factor: number) => void;
  onBalance: (balance: number) => void;
  onPayment: (payment: number) => void;
  onFrequency: (frequency: string) => void;
  onTerm: (term: number) => void;
  /** The fields can't be typed in (a refused save is waiting for the newer offer). */
  disabled?: boolean;
}

export interface RefinanceOfferTermsProps {
  values: RefinanceCalcValues;
  /** Display strings ("" when unknown). */
  payback: string;
  disbursed: string;
  /** Null leaves the row out (a chat card that does not know it). */
  specifiedPercentage: string | null;
  originationFee: string;
  origination: OriginationInput | number;
  /** Why the prior balance cannot be used, under its field. */
  balanceError?: string | null;
  /** Without it the terms read as facts. */
  edit?: RefinanceTermsEdit;
  layout?: OfferTermsLayout;
}

const money = (amount: number) => (amount > 0 ? formatCurrency(amount) : "");

/**
 * A refinance offer's Offer section: funding, factor, payback, disbursed and specified
 * percentage beside the prior balance, payment, frequency, term and the origination fee.
 * Editable with `edit`; the math between the fields is the caller's.
 */
export function RefinanceOfferTerms({
  values,
  payback,
  disbursed,
  specifiedPercentage,
  originationFee,
  origination,
  balanceError,
  edit,
  layout = "page",
}: RefinanceOfferTermsProps) {
  const derived = (
    <>
      <TextFact label="Payback" value={payback} />
      <TextFact label="Disbursed" value={disbursed} />
      {specifiedPercentage !== null && (
        <TextFact label="Specified percentage" value={specifiedPercentage} />
      )}
    </>
  );
  const fees = (
    <>
      <OriginationPct origination={origination} />
      <TextFact label="Origination Fee" value={originationFee} />
    </>
  );
  if (!edit) {
    return (
      <TermsSection title="Offer">
        <SplitColumns
          layout={layout}
          left={
            <>
              <TextFact label="Funding amount" value={money(values.refiAmount)} />
              <TextFact label="Factor" value={values.factor ? String(values.factor) : ""} />
              {derived}
            </>
          }
          right={
            <>
              <FactRow label="Prior balance" error={balanceError}>
                <span className={VALUE_CLASS}>{money(values.balance) || "—"}</span>
              </FactRow>
              <TextFact label="Payment" value={money(values.payment)} />
              <TextFact label="Frequency" value={displayFrequency(values.frequency)} />
              <TextFact label="Term" value={values.term ? String(values.term) : ""} />
              {fees}
            </>
          }
        />
      </TermsSection>
    );
  }
  return (
    <TermsSection title="Offer">
      <SplitColumns
        layout={layout}
        left={
          <>
            <MoneyEdit
              label="Funding amount"
              value={values.refiAmount}
              onChange={edit.onFunding}
              disabled={edit.disabled}
            />
            <NumberEdit
              label="Factor"
              value={values.factor}
              step={OFFER_FACTOR_STEP}
              disabled={edit.disabled}
              onChange={edit.onFactor}
            />
            {derived}
          </>
        }
        right={
          <>
            <MoneyEdit
              label="Prior balance"
              value={values.balance}
              onChange={edit.onBalance}
              error={balanceError}
              disabled={edit.disabled}
            />
            <MoneyEdit
              label="Payment"
              value={values.payment}
              onChange={edit.onPayment}
              disabled={edit.disabled}
            />
            <FactRow label="Frequency">
              <Select
                value={values.frequency}
                disabled={edit.disabled}
                onValueChange={edit.onFrequency}
              >
                <SelectTrigger
                  size="sm"
                  aria-label="Frequency"
                  className="w-full max-w-[9.5rem] text-sm font-semibold"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map(({ value, label }) => (
                    <SelectItem key={value} value={value} className="cursor-pointer">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FactRow>
            <NumberEdit
              label="Term"
              value={values.term}
              disabled={edit.disabled}
              onChange={edit.onTerm}
            />
            {fees}
          </>
        }
      />
    </TermsSection>
  );
}
